import { FC, PointerEvent as ReactPointerEvent, ReactNode, useEffect, useRef, useState } from "react";
import { styled, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { AppRoutes } from "../../config";
import useAuthorization from "../../contexts/useAuthorization";
import { EASE_SMOOTH_OUT } from "../../styles/motion";

// Floating glass tab bar (iOS 26 / Instagram style). The bar floats BOTTOM_NAV_GAP above the
// safe-area inset; pages reserve BOTTOM_NAV_CLEARANCE so their last line clears it, and Explore
// slides it away with BOTTOM_NAV_HIDDEN_TRANSFORM (100% of its own height alone would leave the
// gap + inset showing).
const BOTTOM_NAV_HEIGHT = 60;
const BOTTOM_NAV_GAP = 12;
export const BOTTOM_NAV_CLEARANCE = `calc(${BOTTOM_NAV_HEIGHT + BOTTOM_NAV_GAP * 2}px + env(safe-area-inset-bottom))`;
export const BOTTOM_NAV_HIDDEN_TRANSFORM = `translateY(calc(100% + ${BOTTOM_NAV_GAP}px + env(safe-area-inset-bottom)))`;

// Tab motion follows the docs/testUI tab bar (transitions-dev "Tabs sliding" + "Tab press"):
// - the pill slides between tabs on --tabs-dur / --tabs-ease (250ms, --ease-smooth-out). The
//   reference also tweens the pill's width; our tabs are equal columns, so translateX alone lands
//   it and width never has to animate.
// - the icons fade muted → full on the same clock (the reference's text colour change).
// - a press dips the tab to 0.99 instantly and eases back over --press-dur (150ms). On touch the
//   press is held for at least that long, so a quick tap still shows it in full.
const TABS_MS = 250;
const PRESS_MS = 150;
const TAB_PRESS_SCALE = 0.99;
const ICON_MUTED_OPACITY = 0.78;
// Concentric with the capsule: the pill's radius is the bar's radius minus this padding.
const BAR_PADDING = 4;
const ACTIVE_FILL = "#BC92F8";

// Compact on scroll (iOS 26 tabBarMinimizeBehavior): scrolling down shrinks the bar toward its
// bottom edge, scrolling up (or reaching the top) restores it. A size change between two layout
// states is the transitions-dev "Card resize" lane — 300ms, --ease-smooth-out, the same both ways —
// but done as a scale so only transform animates.
const COMPACT_SCALE = 0.86;
const COMPACT_MS = 300;
// Direction must persist past this many px before the bar changes size, so small jitters and
// momentum wobble don't flip it; within this distance of the top it always stays full size.
const SCROLL_THRESHOLD_PX = 8;
const TOP_OFFSET_PX = 60;

// Touch press hold (the reference's .is-pressed helper): add the class on pointerdown and keep
// it until PRESS_MS after the press began, even if the finger lifts sooner. Mouse presses use
// :active alone.
const pressTimers = new WeakMap<HTMLElement, number>();
const holdPress = (event: ReactPointerEvent<HTMLButtonElement>) => {
  if (event.pointerType === "mouse") {
    return;
  }
  const el = event.currentTarget;
  const startedAt = performance.now();
  window.clearTimeout(pressTimers.get(el));
  el.classList.add("is-pressed");
  const release = () => {
    document.removeEventListener("pointerup", release, true);
    document.removeEventListener("pointercancel", release, true);
    const remaining = Math.max(0, PRESS_MS - (performance.now() - startedAt));
    pressTimers.set(
      el,
      window.setTimeout(() => el.classList.remove("is-pressed"), remaining)
    );
  };
  document.addEventListener("pointerup", release, true);
  document.addEventListener("pointercancel", release, true);
};

const GlobeIcon: FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.125 19.2125C4.90833 18.6875 3.84583 17.9708 2.9375 17.0625C2.02917 16.1542 1.3125 15.0917 0.7875 13.875C0.2625 12.6583 0 11.3625 0 9.9875C0 8.6125 0.2625 7.32083 0.7875 6.1125C1.3125 4.90417 2.02917 3.84583 2.9375 2.9375C3.84583 2.02917 4.90833 1.3125 6.125 0.7875C7.34167 0.2625 8.6375 0 10.0125 0C11.3875 0 12.6792 0.2625 13.8875 0.7875C15.0958 1.3125 16.1542 2.02917 17.0625 2.9375C17.9708 3.84583 18.6875 4.90417 19.2125 6.1125C19.7375 7.32083 20 8.6125 20 9.9875C20 11.3625 19.7375 12.6583 19.2125 13.875C18.6875 15.0917 17.9708 16.1542 17.0625 17.0625C16.1542 17.9708 15.0958 18.6875 13.8875 19.2125C12.6792 19.7375 11.3875 20 10.0125 20C8.6375 20 7.34167 19.7375 6.125 19.2125ZM10 17.95C10.4333 17.35 10.8083 16.725 11.125 16.075C11.4417 15.425 11.7 14.7333 11.9 14H8.1C8.3 14.7333 8.55833 15.425 8.875 16.075C9.19167 16.725 9.56667 17.35 10 17.95ZM7.4 17.55C7.1 17 6.8375 16.4292 6.6125 15.8375C6.3875 15.2458 6.2 14.6333 6.05 14H3.1C3.58333 14.8333 4.1875 15.5583 4.9125 16.175C5.6375 16.7917 6.46667 17.25 7.4 17.55ZM12.6 17.55C13.5333 17.25 14.3625 16.7917 15.0875 16.175C15.8125 15.5583 16.4167 14.8333 16.9 14H13.95C13.8 14.6333 13.6125 15.2458 13.3875 15.8375C13.1625 16.4292 12.9 17 12.6 17.55ZM2.25 12H5.65C5.6 11.6667 5.5625 11.3375 5.5375 11.0125C5.5125 10.6875 5.5 10.35 5.5 10C5.5 9.65 5.5125 9.3125 5.5375 8.9875C5.5625 8.6625 5.6 8.33333 5.65 8H2.25C2.16667 8.33333 2.10417 8.6625 2.0625 8.9875C2.02083 9.3125 2 9.65 2 10C2 10.35 2.02083 10.6875 2.0625 11.0125C2.10417 11.3375 2.16667 11.6667 2.25 12ZM7.65 12H12.35C12.4 11.6667 12.4375 11.3375 12.4625 11.0125C12.4875 10.6875 12.5 10.35 12.5 10C12.5 9.65 12.4875 9.3125 12.4625 8.9875C12.4375 8.6625 12.4 8.33333 12.35 8H7.65C7.6 8.33333 7.5625 8.6625 7.5375 8.9875C7.5125 9.3125 7.5 9.65 7.5 10C7.5 10.35 7.5125 10.6875 7.5375 11.0125C7.5625 11.3375 7.6 11.6667 7.65 12ZM14.35 12H17.75C17.8333 11.6667 17.8958 11.3375 17.9375 11.0125C17.9792 10.6875 18 10.35 18 10C18 9.65 17.9792 9.3125 17.9375 8.9875C17.8958 8.6625 17.8333 8.33333 17.75 8H14.35C14.4 8.33333 14.4375 8.6625 14.4625 8.9875C14.4875 9.3125 14.5 9.65 14.5 10C14.5 10.35 14.4875 10.6875 14.4625 11.0125C14.4375 11.3375 14.4 11.6667 14.35 12ZM13.95 6H16.9C16.4167 5.16667 15.8125 4.44167 15.0875 3.825C14.3625 3.20833 13.5333 2.75 12.6 2.45C12.9 3 13.1625 3.57083 13.3875 4.1625C13.6125 4.75417 13.8 5.36667 13.95 6ZM8.1 6H11.9C11.7 5.26667 11.4417 4.575 11.125 3.925C10.8083 3.275 10.4333 2.65 10 2.05C9.56667 2.65 9.19167 3.275 8.875 3.925C8.55833 4.575 8.3 5.26667 8.1 6ZM3.1 6H6.05C6.2 5.36667 6.3875 4.75417 6.6125 4.1625C6.8375 3.57083 7.1 3 7.4 2.45C6.46667 2.75 5.6375 3.20833 4.9125 3.825C4.1875 4.44167 3.58333 5.16667 3.1 6Z"
      fill="#1F1F1F"
    />
  </svg>
);

const BasketIcon: FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.22222 20L0 6.66667H20L17.7778 20H2.22222ZM4.08333 17.7778H15.9167L17.3333 8.88889H2.66667L4.08333 17.7778ZM7.77778 13.3333H12.2222C12.537 13.3333 12.8009 13.2269 13.0139 13.0139C13.2269 12.8009 13.3333 12.537 13.3333 12.2222C13.3333 11.9074 13.2269 11.6435 13.0139 11.4306C12.8009 11.2176 12.537 11.1111 12.2222 11.1111H7.77778C7.46296 11.1111 7.19907 11.2176 6.98611 11.4306C6.77315 11.6435 6.66667 11.9074 6.66667 12.2222C6.66667 12.537 6.77315 12.8009 6.98611 13.0139C7.19907 13.2269 7.46296 13.3333 7.77778 13.3333ZM3.33333 5.55556C3.01852 5.55556 2.75463 5.44907 2.54167 5.23611C2.3287 5.02315 2.22222 4.75926 2.22222 4.44444C2.22222 4.12963 2.3287 3.86574 2.54167 3.65278C2.75463 3.43982 3.01852 3.33333 3.33333 3.33333H16.6667C16.9815 3.33333 17.2454 3.43982 17.4583 3.65278C17.6713 3.86574 17.7778 4.12963 17.7778 4.44444C17.7778 4.75926 17.6713 5.02315 17.4583 5.23611C17.2454 5.44907 16.9815 5.55556 16.6667 5.55556H3.33333ZM5.55556 2.22222C5.24074 2.22222 4.97685 2.11574 4.76389 1.90278C4.55093 1.68981 4.44444 1.42593 4.44444 1.11111C4.44444 0.796296 4.55093 0.532407 4.76389 0.319444C4.97685 0.106481 5.24074 0 5.55556 0H14.4444C14.7593 0 15.0231 0.106481 15.2361 0.319444C15.4491 0.532407 15.5556 0.796296 15.5556 1.11111C15.5556 1.42593 15.4491 1.68981 15.2361 1.90278C15.0231 2.11574 14.7593 2.22222 14.4444 2.22222H5.55556Z"
      fill="#1F1F1F"
    />
  </svg>
);

// Material Symbols "home" (outlined); the viewBox crops to the glyph so it matches the 20px
// height of its siblings.
const HomeIcon: FC = () => (
  <svg width="18" height="20" viewBox="160 -840 640 720" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M240-200h120v-240h240v240h120v-360L480-740 240-560v360Zm-80 80v-480l320-240 320 240v480H520v-240h-80v240H160Zm320-350Z"
      fill="#1F1F1F"
    />
  </svg>
);

export const GuardianIcon: FC = () => (
  <svg width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.5 3.31348V8.62988C15.5 10.9263 14.781 13.0243 13.3311 14.917C11.9073 16.7754 10.1326 17.9611 8 18.4834C5.86544 17.9513 4.0914 16.7737 2.66895 14.917C1.21888 13.0241 0.500015 10.9363 0.5 8.62988V3.31348L8 0.532227L15.5 3.31348ZM9.79199 5.45215C9.37455 5.45216 8.96226 5.53717 8.57324 5.72266C8.36238 5.81766 8.17218 5.93364 8.00293 6.06543C7.82652 5.93277 7.62887 5.81606 7.41016 5.7207C7.03106 5.55544 6.63224 5.45216 6.20801 5.45215C5.57352 5.45215 4.99799 5.64721 4.50781 6.04004L4.30762 6.2168C3.77053 6.7228 3.50012 7.35868 3.5 8.07324C3.5 8.42822 3.54905 8.77573 3.66797 9.10742C3.78887 9.44461 4.00551 9.80231 4.31641 10.1807V10.1816C4.61317 10.5453 5.03588 10.9845 5.56543 11.4854C6.09399 11.9852 6.79237 12.6176 7.66895 13.3916L8.5 14.125V13.2314C10.1043 11.8319 11.1796 10.8154 11.6748 10.1992C12.2083 9.53538 12.5 8.83108 12.5 8.08398C12.5 7.33326 12.2257 6.73181 11.6963 6.2207C11.1645 5.70738 10.517 5.45215 9.79199 5.45215Z"
      fill="black"
      stroke="black"
    />
  </svg>
);

const TrustAdminIcon: FC = () => (
  <svg width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.41364 14.1081L7.98821 11.5638L11.4943 14.1081L10.1496 9.98046L13.7241 7.31609H9.37954L7.97699 3.18855L6.57444 7.31609H2.22983L5.74716 9.98046L4.40241 14.1081H4.41364ZM8 19C5.67815 18.4431 3.76982 17.1873 2.26407 15.2109C0.758311 13.2454 0 11.0506 0 8.64831V2.85005L8 0L16 2.85005V8.64831C16 11.0506 15.2411 13.2454 13.7354 15.2109C12.2296 17.1873 10.3219 18.4431 8 19Z"
      fill="black"
    />
  </svg>
);

const PatronIcon: FC = () => (
  <svg width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 0L0 2.97293V8.64102C0 11.0482 0.757208 13.2496 2.27188 15.2247C3.78654 17.1997 5.696 18.4651 8 19C10.304 18.4548 12.2135 17.1997 13.7281 15.2247C15.2428 13.2496 16 11.0584 16 8.64102V2.97293L8 0ZM6.90104 12.7249L3.34948 9.2788L4.44792 8.21935L6.97604 10.6573L11.5521 6.24422L12.5974 7.23172L6.90104 12.7249Z"
      fill="black"
    />
  </svg>
);

// Pioneer and Global Trust Admin are the Figma role glyphs (32 × 32 frame, see
// components/RoleIcons); the viewBox crops to the shield so it fills the same 19px height as its
// siblings.
const PioneerIcon: FC = () => (
  <svg width="16" height="19" viewBox="8.46 6.76 15 18.47" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      d="M15.96 25.23C13.8 24.7 12.0098 23.48 10.5898 21.56C9.16984 19.64 8.45996 17.51 8.45996 15.16V9.65002L15.96 6.76001L23.46 9.65002V15.16C23.46 17.5 22.7501 19.64 21.3301 21.56C19.9101 23.48 18.12 24.71 15.96 25.23ZM13.25 19.39H14.71V15.88H19.8301L18.1899 13.68L19.8301 11.49H13.25V19.39Z"
      fill="black"
    />
  </svg>
);

const GlobalTrustAdminIcon: FC = () => (
  <svg width="16" height="19" viewBox="9.04 7.28 13.92 17.39" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16 24.67C13.97 24.16 12.3 22.99 11 21.14C9.7 19.29 9.04004 17.27 9.04004 15.06V9.89001L16 7.28003L22.96 9.89001V15.06C22.96 17.26 22.31 19.29 21 21.14C19.69 22.99 18.03 24.17 16 24.67ZM16 23.32C17.54 22.81 18.8101 21.88 19.8101 20.53C20.8201 19.18 21.4101 17.66 21.5801 15.98H15.9902V8.70001L10.3403 10.81V15.07C10.3403 15.24 10.3401 15.39 10.3501 15.52C10.3601 15.65 10.3804 15.8001 10.4004 15.9901H15.9902V23.34L16 23.32Z"
      fill="black"
    />
  </svg>
);

const TrusteeIcon: FC = () => (
  <svg width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.76839 12.4705H9.23161V10.0941H11.695V7.71773H9.23161V5.34136H6.76839V7.71773H4.30504V10.0941H6.76839V12.4705ZM8 19C5.6949 18.455 3.78535 17.1905 2.27121 15.2174C0.757078 13.2444 0 11.0425 0 8.63339V2.965L8 0L16 2.965V8.63339C16 11.0425 15.2429 13.2444 13.7288 15.2174C12.2147 17.1905 10.3051 18.455 8 19Z"
      fill="black"
    />
  </svg>
);

type Tab = { label: string; path: string; active: boolean; icon: ReactNode };

export const BottomNavBar: FC = () => {
  const navigator = useNavigate();
  const { loggedIn, isGuardian, isPioneer, isAdmin, isGlobalTrustAdmin, isTrust } = useAuthorization();
  const location = useLocation();
  const theme = useTheme();
  const barShown = useMediaQuery(theme.breakpoints.down("md"));
  const [compact, setCompact] = useState(false);

  // Scroll direction → compact. CLAUDE.md prefers IntersectionObserver for scroll work, but IO
  // can't report direction; this listener only flips a boolean (React bails on an unchanged
  // value), and the size change itself is a one-shot CSS transition, never scrubbed by scroll.
  useEffect(() => {
    if (!barShown) {
      setCompact(false);
      return undefined;
    }
    // Clamped so iOS rubber-banding past either end doesn't read as a direction change.
    const scrollY = () =>
      Math.min(Math.max(window.scrollY, 0), document.documentElement.scrollHeight - window.innerHeight);
    let anchorY = scrollY();
    let ticking = false;
    const onScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = scrollY();
        const delta = y - anchorY;
        if (y < TOP_OFFSET_PX) {
          setCompact(false);
          anchorY = y;
        } else if (Math.abs(delta) > SCROLL_THRESHOLD_PX) {
          // The anchor only moves once the threshold is crossed, so a slow scroll still adds up.
          setCompact(delta > 0);
          anchorY = y;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [barShown]);

  // A new page starts with the full bar.
  useEffect(() => setCompact(false), [location.pathname]);

  const tabs: Tab[] = [
    {
      label: "Home",
      path: AppRoutes.EcoBank.path,
      active: location.pathname === AppRoutes.EcoBank.path,
      icon: <HomeIcon />,
    },
    { label: "Explore", path: "/explore", active: location.pathname.startsWith("/explore"), icon: <GlobeIcon /> },
  ];
  if (loggedIn) {
    tabs.push(
      {
        label: "Market",
        path: "/dashboard/portfolio",
        active: location.pathname.startsWith("/dashboard/portfolio"),
        icon: <BasketIcon />,
      },
      {
        label: "Account",
        path: "/dashboard/wallet",
        active: location.pathname.startsWith("/dashboard/wallet"),
        icon: (
          <>
            {isGuardian && <GuardianIcon />}
            {isPioneer && <PioneerIcon />}
            {isGlobalTrustAdmin && <GlobalTrustAdminIcon />}
            {isAdmin && !isGlobalTrustAdmin && <TrustAdminIcon />}
            {isTrust && <TrusteeIcon />}
            {!isGuardian && !isPioneer && !isAdmin && !isTrust && <PatronIcon />}
          </>
        ),
      }
    );
  }
  const activeIndex = tabs.findIndex((tab) => tab.active);
  // On routes with no tab the pill fades out where it was, and slides on from there next time.
  const lastIndex = useRef(Math.max(activeIndex, 0));
  if (activeIndex >= 0) {
    lastIndex.current = activeIndex;
  }

  return (
    <NavBarWrapper id="eco-bottom-navbar" aria-label="Primary">
      {/* The shrink lives on this inner surface: Explore writes its own slide-away transform on
          the nav itself, and Explore's search tracks the nav's rect, which stays put. */}
      <NavSurface data-compact={compact || undefined}>
        <TabTrack style={{ gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}>
          {/* One column-wide pill, so translateX(index * 100%) lands on the active column without
            measuring. */}
          <TabPill
            aria-hidden="true"
            style={{
              width: `calc((100% - ${BAR_PADDING * 2}px) / ${tabs.length})`,
              transform: `translateX(${lastIndex.current * 100}%)`,
              opacity: activeIndex >= 0 ? 1 : 0,
            }}
          />
          {tabs.map((tab) => (
            <TabButton
              key={tab.label}
              type="button"
              aria-label={tab.label}
              aria-current={tab.active ? "page" : undefined}
              onPointerDown={holdPress}
              onClick={() => navigator(tab.path)}
            >
              <span>{tab.icon}</span>
            </TabButton>
          ))}
        </TabTrack>
      </NavSurface>
    </NavBarWrapper>
  );
};

const NavBarWrapper = styled("nav")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "block",
    position: "fixed",
    left: 16,
    right: 16,
    bottom: `calc(${BOTTOM_NAV_GAP}px + env(safe-area-inset-bottom))`,
    height: BOTTOM_NAV_HEIGHT,
    zIndex: 1100,
  },
}));

const NavSurface = styled("div")(({ theme }) => ({
  height: "100%",
  borderRadius: 999,
  transformOrigin: "50% 100%",
  transition: `transform ${COMPACT_MS}ms ${EASE_SMOOTH_OUT}`,
  "&[data-compact]": {
    transform: `scale(${COMPACT_SCALE})`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
  overflow: "hidden",
  // iOS glass: translucent fill + blur/saturate of whatever scrolls underneath, a light inner
  // rim and a soft lift shadow.
  backgroundColor: "rgba(250, 250, 250, 0.62)",
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  boxShadow: [
    "inset 0 1px 0 rgba(255, 255, 255, 0.75)",
    "inset 0 0 0 1px rgba(255, 255, 255, 0.35)",
    "0 8px 24px rgba(0, 0, 0, 0.12)",
    "0 1px 3px rgba(0, 0, 0, 0.08)",
  ].join(", "),
  "@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))": {
    backgroundColor: "#F5F5F5",
  },
  "@media (prefers-reduced-transparency: reduce)": {
    backgroundColor: "#F5F5F5",
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(28, 28, 30, 0.6)",
    boxShadow:
      "inset 0 1px 0 rgba(255, 255, 255, 0.14), inset 0 0 0 1px rgba(255, 255, 255, 0.08), 0 8px 24px rgba(0, 0, 0, 0.4)",
    // The glyphs carry a hard-coded dark fill; lift the inactive ones off the dark glass (the
    // active one sits on the light purple pill and keeps its fill).
    "& button:not([aria-current]) svg path": { fill: "#F5F5F5" },
    "@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))": {
      backgroundColor: "#1C1C1E",
    },
    "@media (prefers-reduced-transparency: reduce)": {
      backgroundColor: "#1C1C1E",
    },
  }),
}));

const TabTrack = styled("div")({
  position: "relative",
  display: "grid",
  height: "100%",
  padding: BAR_PADDING,
  boxSizing: "border-box",
});

const TabPill = styled("div")({
  position: "absolute",
  top: BAR_PADDING,
  bottom: BAR_PADDING,
  left: BAR_PADDING,
  // Width is set inline to one column of the padded track (an absolute box's % resolves against
  // the padding box, hence the subtraction), so each translateX(100%) step is exactly one column.
  borderRadius: 999,
  backgroundColor: ACTIVE_FILL,
  pointerEvents: "none",
  transition: `transform ${TABS_MS}ms ${EASE_SMOOTH_OUT}, opacity ${TABS_MS}ms ${EASE_SMOOTH_OUT}`,
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
});

// Each tab fills its whole column, so there are no dead gaps between targets.
const TabButton = styled("button")({
  position: "relative",
  appearance: "none",
  border: 0,
  margin: 0,
  padding: 0,
  background: "transparent",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: 999,
  WebkitTapHighlightColor: "transparent",
  touchAction: "manipulation",
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
  // Resting rule = the release; the press below swaps in an instant dip.
  transition: `transform ${PRESS_MS}ms ${EASE_SMOOTH_OUT}`,
  "&:active, &.is-pressed": {
    transform: `scale(${TAB_PRESS_SCALE})`,
    transition: "transform 0ms",
  },
  "& > span": {
    display: "flex",
    opacity: ICON_MUTED_OPACITY,
    transition: `opacity ${TABS_MS}ms ${EASE_SMOOTH_OUT}`,
  },
  "&[aria-current] > span": { opacity: 1 },
  "@media (hover: hover) and (pointer: fine)": {
    "&:not([aria-current]):hover > span": { opacity: 1 },
  },
  "&:focus-visible": {
    outline: `2px solid ${ACTIVE_FILL}`,
    outlineOffset: -2,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "& > span": { transition: "none" },
    "&:active, &.is-pressed": { transform: "none" },
  },
});

export default BottomNavBar;
